import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/verify-recovery-code', { recovery_code: recoveryCode });
      toast.success('Recovery code verified!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid recovery code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { recovery_code: recoveryCode, email, new_password: newPassword });
      toast.success('Password reset successfully! You can now login.');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: 'var(--bg-void)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', '--tw-ring-color': 'var(--amber)' };
  const btnStyle = { background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)', letterSpacing: '0.1em' };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 relative" style={{ background: 'var(--bg-void)' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-10" style={{ background: 'var(--amber)', filter: 'blur(120px)' }} />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/workforce-logo.png" alt="WorkForce" className="mx-auto" style={{ height: '108px', width: 'auto' }} />
        </div>

        <div className="mb-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium hover:underline" style={{ color: 'var(--amber)' }}>
            <ArrowLeft className="h-4 w-4" /> Back to Login
          </Link>
        </div>

        <div className="rounded-xl p-6 sm:p-8" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          {step === 1 ? (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Forgot Password</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Enter your admin recovery code to reset password</p>
              </div>
              <div className="space-y-2">
                <Input
                  type="text" placeholder="Enter recovery code" value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)} required
                  className="h-11 px-4 rounded-lg text-sm border focus:outline-none focus:ring-2 font-mono"
                  style={inputStyle} data-testid="recovery-code-input"
                />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Recovery codes are 10 characters long</p>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-bold tracking-wider uppercase rounded-lg hover:brightness-110" style={btnStyle} disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Reset Password</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Enter user email and new password</p>
              </div>
              <Input type="email" placeholder="User email address" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 px-4 rounded-lg text-sm border focus:outline-none focus:ring-2" style={inputStyle} />
              <Input type="password" placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="h-11 px-4 rounded-lg text-sm border focus:outline-none focus:ring-2" style={inputStyle} />
              <Input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11 px-4 rounded-lg text-sm border focus:outline-none focus:ring-2" style={inputStyle} />
              <Button type="submit" className="w-full h-12 text-base font-bold tracking-wider uppercase rounded-lg hover:brightness-110" style={btnStyle} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
